import type { StorageAdapter, StorageMode } from './types';
import { LocalStorageAdapter } from './localStorageAdapter';
import { RemoteApiAdapter } from './remoteApiAdapter';

export type { StorageAdapter, StoragePayload, StorageMode } from './types';
export { LocalStorageAdapter } from './localStorageAdapter';
export { RemoteApiAdapter } from './remoteApiAdapter';

// localStorage key for the device ID (never cleared by "重設")
const KEY_DEVICE_ID = 'dmy_device_id';

/**
 * Generate a UUID v4 for device identification.
 * Uses crypto.randomUUID() when available (modern browsers), falls back to
 * a manual implementation for older environments.
 */
function generateDeviceId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: manual UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Retrieve or create a stable device ID stored in localStorage.
 * The device ID survives "重設" so the same device can always be identified.
 */
function getOrCreateDeviceId(): string {
  try {
    if (typeof localStorage !== 'undefined') {
      const existing = localStorage.getItem(KEY_DEVICE_ID);
      if (existing) return existing;
      const newId = generateDeviceId();
      localStorage.setItem(KEY_DEVICE_ID, newId);
      return newId;
    }
  } catch {
    // SSR or restricted env
  }
  return generateDeviceId();
}

export interface StorageInitResult {
  adapter: StorageAdapter;
  mode: StorageMode;
  /** LocalStorageAdapter instance, always available for font-size persistence */
  localAdapter: LocalStorageAdapter;
}

/**
 * Factory function: determine which adapter to use and return it.
 *
 * Decision logic:
 *  1. If VITE_API_BASE_URL is not set → LocalStorageAdapter
 *  2. If set → probe GET /api/health
 *     - 200 OK  → RemoteApiAdapter
 *     - failure → LocalStorageAdapter (silent fallback)
 */
export async function initStorage(): Promise<StorageInitResult> {
  const localAdapter = new LocalStorageAdapter();
  const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const apiToken = import.meta.env.VITE_API_TOKEN as string | undefined;

  if (!apiBase) {
    return { adapter: localAdapter, mode: 'local', localAdapter };
  }

  const healthy = await RemoteApiAdapter.checkHealth(apiBase, apiToken);
  if (!healthy) {
    console.warn('[Storage] Remote API unavailable, falling back to localStorage');
    return { adapter: localAdapter, mode: 'local', localAdapter };
  }

  const deviceId = getOrCreateDeviceId();
  const remoteAdapter = new RemoteApiAdapter(apiBase, apiToken, deviceId);
  console.info(`[Storage] Using remote API: ${apiBase} (deviceId: ${deviceId})`);
  return { adapter: remoteAdapter, mode: 'remote', localAdapter };
}
